import { Box, Drawer, List, Typography } from '@mui/material';
import { AppBar } from '@mui/material';
import { FC, useCallback, useEffect, useState } from 'react';
import { Navigate, Outlet, useNavigate } from 'react-router-dom';

import logoMobile from 'src/assets/images/logo.svg';
import logo from 'src/assets/images/mainlogo.svg';
import { Footer } from 'src/components/Footer';
import { MyProfileMenu } from 'src/components/MyProfileMenu';
import { useSelector } from 'src/services/redux';
import { Notifications } from 'src/services/user/components/Notifications';
import { RoleGuard } from 'src/services/user/RoleGuard';
import { theme } from '../../services/theme';
import { BoxRow } from '../BoxRow';
import { DrawerLink } from './DrawerLink';
import { useDrawerLinks } from './useDrawerLinks';

export const MainLayout: FC = ({ children }) => {
  const userFirstName = useSelector((store) => store.user?.firstName);
  const userLastName = useSelector((store) => store.user?.lastName);
  const drawerLinks = useDrawerLinks();
  const [isMenuOpen, setMenuOpen] = useState<boolean>(false);

  const toggleMenu = useCallback(() => {
    if (window.screen.width < 768) {
      setMenuOpen((prev) => !prev);
    }
  }, []);

  const navigate = useNavigate();

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  }, [isMenuOpen]);

  return (
    <>
      <AppBar
        sx={{
          backgroundColor: '#ffffff',
          height: '100px',
          justifyContent: 'center',
          padding: '0px 32px',
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.25)',
          [theme.breakpoints.down('lg')]: {
            padding: '0px 25px',
            height: '70px',
          },
          [theme.breakpoints.down('md')]: {
            padding: '0px 20px',
            height: '55px',
          },
          [theme.breakpoints.down('md')]: {
            padding: '0px 20px',
            height: '55px',
          },
          [theme.breakpoints.down('sm')]: {
            padding: '0px 15px',
            height: '50px',
          },
          [theme.breakpoints.down('mobile')]: {
            padding: '0px 10px',
            height: '45px',
          },
        }}
      >
        <BoxRow
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
            marginLeft: '220px',
            [theme.breakpoints.down('lg')]: {
              marginLeft: '180px',
            },
            [theme.breakpoints.down('md')]: {
              marginLeft: '80px',
            },
            [theme.breakpoints.down('sm')]: {
              marginLeft: '0',
            },
          }}
        >
          <Box
            onClick={toggleMenu}
            sx={{
              width: '42px',
              height: '42px',
              border: '1px solid #9AB1C0',
              borderRadius: '3px',
              cursor: 'pointer',
              display: 'none',
              padding: '8px 0',
              [theme.breakpoints.down('sm')]: {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexWrap: 'wrap',
                width: '37px',
                height: '37px',
                padding: isMenuOpen ? '7px 0 9px' : '7px 0 5px',
              },
              [theme.breakpoints.down('mobile')]: {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexWrap: 'wrap',
                width: '34px',
                height: '34px',
                padding: isMenuOpen ? '7px 0 6px' : '7px 0',
              },
            }}
          >
            <Box
              sx={{
                width: '30px',
                height: '4px',
                transition: '0.4s ease',
                background: '#9AB1C0',
                m: isMenuOpen ? '8px 0 0' : '1px 0',
                transform: isMenuOpen ? 'rotate(-138deg)' : 'none',
                [theme.breakpoints.down('sm')]: {
                  width: '24px',
                  height: '2px',
                },
                [theme.breakpoints.down('mobile')]: {
                  m: isMenuOpen ? '7px 0 0' : '1px 0',
                },
              }}
            />
            <Box
              sx={{
                width: '30px',
                height: '4px',
                background: '#9AB1C0',
                m: '1px 0',
                display: isMenuOpen ? 'none' : 'block',
                [theme.breakpoints.down('sm')]: {
                  width: '24px',
                  height: '2px',
                },
                [theme.breakpoints.down('mobile')]: {},
              }}
            />
            <Box
              sx={{
                width: '30px',
                height: '4px',
                transition: '0.4s ease',
                background: '#9AB1C0',
                m: isMenuOpen ? '-11px 0 0' : '1px 0',
                transform: isMenuOpen ? 'rotate(138deg)' : 'none',
                [theme.breakpoints.down('sm')]: {
                  width: '24px',
                  height: '2px',
                },
                [theme.breakpoints.down('mobile')]: {
                  m: isMenuOpen ? '-12px 0 0' : '1px 0',
                },
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', flex: 1, padding: '0 10px' }}>
            {userFirstName && userLastName ? (
              <Typography
                sx={{
                  color: '#356482',
                  fontSize: '24px',
                  fontWeight: '600',
                  [theme.breakpoints.down('sm')]: {
                    fontSize: '18px',
                    lineHeight: '20px',
                  },
                  [theme.breakpoints.down('mobile')]: {
                    fontSize: '16px',
                    lineHeight: '18px',
                  },
                }}
              >
                Hello, {userFirstName} {userLastName}
              </Typography>
            ) : (
              <Box />
            )}
          </Box>
          <RoleGuard disableForSuperAdmin roles={['ATTORNEY', 'FIRM_ADMIN']}>
            <Notifications />
          </RoleGuard>
          <MyProfileMenu />
        </BoxRow>
      </AppBar>
      <Drawer
        anchor="left"
        sx={{
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: '220px',
            backgroundColor: 'primary.main',
            paddingLeft: '6px',
            paddingRight: '11px',
            borderRight: 'none',
          },
          [theme.breakpoints.down('lg')]: {
            '& .MuiDrawer-paper': {
              width: '180px',
            },
          },
          [theme.breakpoints.down('md')]: {
            '& .MuiDrawer-paper': {
              width: '80px',
              paddingRight: '6px',
            },
          },
          [theme.breakpoints.down('sm')]: {
            '& .MuiDrawer-paper': {
              width: '180px',
              top: '55px',
              paddingRight: '6px',
              position: 'fixed',
              left: isMenuOpen ? '0' : '-100%',
              transition: '0.4s ease',
            },
          },
          [theme.breakpoints.down('sm')]: {
            '& .MuiDrawer-paper': {
              top: '50px',
              width: '180px',
              left: isMenuOpen ? '0' : '-100%',
              position: 'fixed',
              transition: '0.4s ease',
              paddingRight: '6px',
            },
          },
          [theme.breakpoints.down('mobile')]: {
            '& .MuiDrawer-paper': {
              top: '45px',
              width: '180px',
              left: isMenuOpen ? '0' : '-100%',
              position: 'fixed',
              transition: '0.4s ease',
              paddingRight: '6px',
            },
          },
        }}
        variant="permanent"
      >
        <Box
          onClick={() => navigate('/')}
          sx={{
            cursor: 'pointer',
            marginTop: '42px',
            marginBottom: '41px',
            display: 'flex',
            justifyContent: 'center',
            '& .mobile-logo': {
              display: 'none',
            },
            [theme.breakpoints.down('lg')]: {
              margin: '30px 0',
            },
            [theme.breakpoints.down('md')]: {
              margin: '25px 0',
              width: '100%',
              '& .logo-img': {
                display: 'none',
              },
              '& .mobile-logo': {
                display: 'block',
              },
            },
            [theme.breakpoints.down('sm')]: {
              '& .mobile-logo': {
                display: 'block',
                maxWidth: '50px',
                width: '100%',
                margin: '0 auto',
              },
            },
            [theme.breakpoints.down('mobile')]: {
              margin: '15px 0 10px',
            },
          }}
        >
          <img
            src={logo}
            className="logo-img"
            style={{
              maxWidth: '200px',
              width: '100%',
            }}
            alt="logo"
          />
          <img
            src={logoMobile}
            className="mobile-logo"
            style={{
              width: '100%',
            }}
            alt="logo"
          />
        </Box>
        <List>
          {drawerLinks.map((item, index) => (
            <DrawerLink key={index} {...item} toggleMenu={toggleMenu} />
          ))}
        </List>
      </Drawer>
      <Box
        component="main"
        sx={{
          backgroundColor: '#ffffff',
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          marginLeft: '220px',
          marginTop: '100px',
          padding: '12px 24px',
          height: 'calc(100vh - 100px)',
          boxSizing: 'border-box',
          overflowY: 'auto',
          [theme.breakpoints.down('xl')]: {
            padding: '12px 21px',
          },
          [theme.breakpoints.down('lg')]: {
            marginTop: '70px',
            marginLeft: '180px',
            padding: '12px 16px',
            height: 'calc(100vh - 70px)',
          },
          [theme.breakpoints.down('md')]: {
            marginLeft: '80px',
            marginTop: '55px',
            padding: '12px',
            height: 'calc(100vh - 55px)',
          },
          [theme.breakpoints.down('sm')]: {
            padding: '12px 10px',
            marginLeft: '0',
          },
          [theme.breakpoints.down('mobile')]: {
            padding: '12px 8px',
          },
        }}
      >
        <Box sx={{ flex: '1 0 auto' }}>
          {children || <Outlet />}
        </Box>
        <Footer />
      </Box>
      {isMenuOpen && (
        <Box
          onClick={toggleMenu}
          sx={{
            zIndex: '1000',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            backgroundColor: isMenuOpen ? '#356482' : 'transparent',
            transition: '0.4s ease',
            opacity: isMenuOpen ? '0.7' : '0',
            flexGrow: 1,
            minHeight: '100%',
            height: '10000%',
            display: 'block',
          }}
        />
      )}
    </>
  );
};
